import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import {
  getSidebarStatus,
  setSidebarStatus,
  setLanguage,
  setTheme,
} from '@/utils/localstorage';
import { getLocale } from '@/plugins/i18n';
import store from '@/store';
import { getInitialTheme } from '@/utils/app';

export class AppProgress {
  public active = false;
  public bufferValue = 100;
  public color = 'primary';
  public indeterminate = true;
  public value = 0;
}

export enum AppTheme {
  Light = 'light',
  Dark = 'dark',
}

export interface IAppState {
  overlay: boolean;
  progress: AppProgress;
  sidebar: {
    opened: boolean;
    withoutAnimation: boolean;
  };
  language: string;
}

@Module({
  dynamic: true,
  name: 'app',
  store,
})
class AppStore extends VuexModule implements IAppState {
  public overlay = false;
  public progress = new AppProgress();
  public sidebar = {
    opened: getSidebarStatus() !== 'closed',
    withoutAnimation: false,
  };
  public language = getLocale();
  public theme = getInitialTheme();

  @Mutation
  private SHOW_OVERLAY() {
    this.overlay = true;
  }

  @Mutation
  private HIDE_OVERLAY() {
    this.overlay = false;
  }

  @Mutation
  private START_PROGRESS() {
    this.progress.active = true;
  }

  @Mutation
  private STOP_PROGRESS() {
    this.progress = new AppProgress();
  }

  @Mutation
  private CONFIGURE_PROGRESS(config: Partial<AppProgress>) {
    if (config.active !== void 0) {
      this.progress.active = config.active!;
    }
    if (config.bufferValue !== void 0) {
      this.progress.bufferValue = config.bufferValue!;
    }
    if (config.color !== void 0) {
      this.progress.color = config.color!;
    }
    if (config.indeterminate !== void 0) {
      this.progress.indeterminate = config.indeterminate!;
    }
    if (config.value !== void 0) {
      this.progress.value = config.value!;
    }
  }

  @Mutation
  private TOGGLE_SIDEBAR(withoutAnimation: boolean) {
    this.sidebar.opened = !this.sidebar.opened;
    this.sidebar.withoutAnimation = withoutAnimation;
    if (this.sidebar.opened) {
      setSidebarStatus('opened');
    } else {
      setSidebarStatus('closed');
    }
  }

  @Mutation
  private CLOSE_SIDEBAR(withoutAnimation: boolean) {
    this.sidebar.opened = false;
    this.sidebar.withoutAnimation = withoutAnimation;
    setSidebarStatus('closed');
  }

  @Mutation
  private SET_LANGUAGE(language: string) {
    this.language = language;
    setLanguage(this.language);
  }

  @Mutation
  private SET_THEME(theme: AppTheme) {
    this.theme = theme;
    setTheme(this.theme);
  }

  @Action
  public ShowOverlay() {
    this.SHOW_OVERLAY();
  }

  @Action
  public HideOverlay() {
    this.HIDE_OVERLAY();
  }

  @Action
  public StartProgress() {
    this.START_PROGRESS();
  }

  @Action
  public StopProgress() {
    this.STOP_PROGRESS();
  }

  @Action
  public StartProgressWithConfig(config: Partial<AppProgress>) {
    this.CONFIGURE_PROGRESS(config);
    this.START_PROGRESS();
  }

  @Action
  public ToggleSideBar(withoutAnimation?: boolean) {
    this.TOGGLE_SIDEBAR(withoutAnimation === void 0 ? false : withoutAnimation);
  }

  @Action
  public CloseSideBar(withoutAnimation?: boolean) {
    this.CLOSE_SIDEBAR(withoutAnimation === void 0 ? false : withoutAnimation);
  }

  @Action
  public SetLanguage(language: string) {
    this.SET_LANGUAGE(language);
  }

  @Action
  public SetTheme(theme: AppTheme) {
    this.SET_THEME(theme);
  }
}

export const AppModule = getModule(AppStore);
